/**
 * Agile AI planning helpers (pure, deterministic, zero I/O).
 *
 * These are the reasoning primitives behind the Section 10 agile AI tools
 * registered in aiService.ts (draft_backlog_from_scope, split_epic,
 * suggest_acceptance_criteria, estimate_story_points, plan_sprint,
 * refine_backlog, detect_blockers, summarize_sprint, agile_board_query).
 *
 * They are split out of aiService.ts so the logic is unit-testable without
 * pulling in the provider bootstrap, and so both the tool handlers (execute)
 * and the mock provider (preview) share one implementation. Nothing here
 * touches Supabase, the store, or the network: give it work items / sprints /
 * links and it returns a plan or an analysis.
 *
 * Vocabulary is generic Scrum / SAFe terminology only (Epic, Feature, Story,
 * Sprint, Board, Program Increment). No commercial product names, ever.
 * No em-dashes in any generated copy (standing rule).
 *
 * Part of the Hybrid Waterfall/Agile Delivery feature (P6 AI).
 */

import type {
  WorkItem,
  WorkItemLink,
  Sprint,
  Priority,
  WorkItemType,
} from '@/types/agile'
import { isDefinitionOfReady, STORY_POINT_SCALE } from '@/types/agile'

// ── Small shared utilities ───────────────────────────────────────────────

const PRIORITY_RANK: Record<Priority, number> = { critical: 0, high: 1, medium: 2, low: 3 }

/** Snap an arbitrary number to the nearest Fibonacci story-point value. */
export function nearestStoryPoint(n: number): number {
  let best = STORY_POINT_SCALE[0] as number
  let bestDelta = Math.abs(n - best)
  for (const p of STORY_POINT_SCALE) {
    const d = Math.abs(n - p)
    if (d < bestDelta) {
      best = p
      bestDelta = d
    }
  }
  return best
}

/** Title-case a short phrase for a generated item title. */
function titleCase(s: string): string {
  return s
    .trim()
    .split(/\s+/)
    .map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(' ')
}

/** Normalise a title for duplicate detection (lowercase, alnum + single spaces). */
function normaliseTitle(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
}

// ── draft_backlog_from_scope: scaffold an Epic -> Feature -> Story tree ───

export interface DraftStory {
  title: string
  description?: string
  acceptanceCriteria: string[]
  storyPoints?: number
}
export interface DraftFeature {
  title: string
  description?: string
  stories: DraftStory[]
}
export interface DraftEpic {
  title: string
  description?: string
  acceptanceCriteria?: string[]
  features: DraftFeature[]
}

/**
 * Produce a sensible starter Epic -> Feature -> Story tree from a free-text
 * scope. Deterministic: the same scope always yields the same tree. This is
 * the fallback the mock provider uses to draft a confirmable backlog; a real
 * LLM (Claude mode) passes its own structured tree straight to the tool and
 * this is not called.
 */
export function scaffoldBacklogTree(scope: string): DraftEpic[] {
  const clean = (scope ?? '').trim()
  const epicTitle = clean
    ? titleCase(clean.split(/[.\n;]/)[0]!.split(/\s+/).slice(0, 8).join(' '))
    : 'Delivery Scope'

  // Break the scope into up to three themes on natural boundaries.
  const rawThemes = clean
    .split(/[.\n;]|,\s|\band\b/i)
    .map((t) => t.trim())
    .filter((t) => t.length > 3)
  const themes = (rawThemes.length > 0 ? rawThemes : ['Core capability']).slice(0, 3)

  const stdSuffix = ['Design and setup', 'Build and integrate']

  const features: DraftFeature[] = themes.map((theme, i) => {
    const featureTitle = titleCase(theme.split(/\s+/).slice(0, 6).join(' '))
    const stories: DraftStory[] = stdSuffix.map((suffix) => {
      const storyTitle = `${featureTitle}: ${suffix}`
      return {
        title: storyTitle,
        acceptanceCriteria: suggestAcceptanceCriteria(storyTitle),
        storyPoints: suffix === stdSuffix[0] ? 3 : 5,
      }
    })
    return {
      title: featureTitle,
      description: i === 0 ? clean || undefined : undefined,
      stories,
    }
  })

  // Always add an acceptance / closeout feature so the tree ends on validation.
  features.push({
    title: 'Validation and acceptance',
    stories: [
      {
        title: 'Validation and acceptance: verify against acceptance criteria',
        acceptanceCriteria: suggestAcceptanceCriteria('verify the delivered scope against its acceptance criteria'),
        storyPoints: 2,
      },
    ],
  })

  return [
    {
      title: epicTitle,
      description: clean || undefined,
      acceptanceCriteria: [
        'All child features and stories are Done and accepted.',
        'The scope is demonstrated to stakeholders and signed off.',
      ],
      features,
    },
  ]
}

/** Count the total items a draft tree will create (for a preview summary). */
export function countDraftTree(epics: DraftEpic[]): {
  epics: number
  features: number
  stories: number
  total: number
} {
  let epicN = 0
  let featureN = 0
  let storyN = 0
  for (const e of epics) {
    epicN++
    for (const f of e.features) {
      featureN++
      storyN += f.stories.length
    }
  }
  return { epics: epicN, features: featureN, stories: storyN, total: epicN + featureN + storyN }
}

// ── split_epic: decompose an epic/feature into child stories ─────────────

/**
 * Propose child stories for an epic or feature. Deterministic scaffold used
 * when the caller does not supply its own story list.
 */
export function scaffoldEpicStories(parent: Pick<WorkItem, 'title' | 'description'>): DraftStory[] {
  const base = titleCase((parent.title ?? 'Epic').split(/\s+/).slice(0, 6).join(' '))
  const phases = [
    { suffix: 'Discovery and requirements', pts: 3 },
    { suffix: 'Core implementation', pts: 5 },
    { suffix: 'Integration and data', pts: 5 },
    { suffix: 'Validation and acceptance', pts: 2 },
  ]
  return phases.map((p) => {
    const title = `${base}: ${p.suffix}`
    return {
      title,
      acceptanceCriteria: suggestAcceptanceCriteria(title),
      storyPoints: p.pts,
    }
  })
}

// ── suggest_acceptance_criteria: Given / When / Then ─────────────────────

/**
 * Produce Given / When / Then style acceptance criteria for a story. Returns
 * an ordered list of full sentences (one happy path plus one guard path).
 */
export function suggestAcceptanceCriteria(storyTitle: string): string[] {
  const subject = (storyTitle ?? 'the story').replace(/^[a-z]/, (c) => c.toLowerCase()).trim() || 'the story'
  return [
    `Given a user with the right permissions, when they perform ${subject}, then the action completes and the result is persisted.`,
    `Given required inputs are missing or invalid, when the user attempts ${subject}, then a clear validation message is shown and no change is saved.`,
    `Given the action succeeds, when the user returns to the relevant view, then the change is visible and reflected in any dependent totals.`,
  ]
}

// ── estimate_story_points: relative sizing from comparables ──────────────

export interface EstimateResult {
  suggested: number
  rationale: string
  comparables: { key: string; title: string; storyPoints: number }[]
}

/**
 * Suggest a story-point value for a target item by comparing it to recent
 * estimated items of the same type. Falls back to a heuristic on the title
 * when there are no comparables.
 */
export function suggestPoints(
  target: Pick<WorkItem, 'title' | 'itemType'>,
  allItems: WorkItem[],
): EstimateResult {
  const estimated = allItems.filter(
    (i) => typeof i.storyPoints === 'number' && (i.storyPoints as number) > 0,
  )
  const sameType = estimated.filter((i) => i.itemType === target.itemType)
  const pool = (sameType.length >= 2 ? sameType : estimated).slice()

  // Complexity signal from the title.
  const t = (target.title ?? '').toLowerCase()
  const heavy = /(migrat|integrat|research|spike|refactor|architecture|end.to.end|security)/.test(t)
  const light = /(copy|label|text|rename|tweak|config|toggle|typo)/.test(t)

  let suggested: number
  let rationale: string
  if (pool.length > 0) {
    const pts = pool.map((i) => i.storyPoints as number).sort((a, b) => a - b)
    const median = pts[Math.floor(pts.length / 2)]!
    const factor = heavy ? 1.6 : light ? 0.5 : 1
    suggested = nearestStoryPoint(median * factor)
    rationale =
      `Median of ${pool.length} comparable estimated ${target.itemType === 'bug' ? 'bugs' : 'items'} is ${median} point(s). ` +
      (heavy
        ? 'The title suggests integration or research complexity, so the estimate is sized up.'
        : light
          ? 'The title suggests a small, well understood change, so the estimate is sized down.'
          : 'The title reads as a typical item, so the estimate tracks the comparable median.')
  } else {
    suggested = heavy ? 8 : light ? 2 : 3
    rationale =
      'No comparable estimated items were found, so this is a first-pass size from the title only. ' +
      'Refine it in Planning Poker once the team has a few reference stories.'
  }

  return {
    suggested,
    rationale,
    comparables: pool
      .slice()
      .sort((a, b) => (b.storyPoints as number) - (a.storyPoints as number))
      .slice(0, 3)
      .map((i) => ({ key: i.key, title: i.title, storyPoints: i.storyPoints as number })),
  }
}

// ── plan_sprint: fill a sprint to capacity ───────────────────────────────

export interface SprintPlanItem {
  id: string
  key: string
  title: string
  itemType: WorkItemType
  priority: Priority
  storyPoints: number
  ready: boolean
}
export interface SprintPlan {
  capacityPoints: number
  totalPoints: number
  selected: SprintPlanItem[]
  skippedUnestimated: { id: string; key: string; title: string }[]
}

/**
 * Select backlog items to fill a sprint to capacity, ordered by priority then
 * backlog rank. Only unassigned, not-Done, deliverable items (story / task /
 * bug / spike) are eligible; unestimated ones are surfaced separately so the
 * planner can size them first. Includes at least one item when any fit.
 */
export function selectSprintPlan(backlog: WorkItem[], capacityPoints: number): SprintPlan {
  const eligible = backlog.filter(
    (i) =>
      !i.sprintId &&
      i.statusCategory !== 'done' &&
      (i.itemType === 'story' || i.itemType === 'task' || i.itemType === 'bug' || i.itemType === 'spike'),
  )

  const skippedUnestimated = eligible
    .filter((i) => !(typeof i.storyPoints === 'number' && (i.storyPoints as number) > 0))
    .map((i) => ({ id: i.id, key: i.key, title: i.title }))

  const estimated = eligible
    .filter((i) => typeof i.storyPoints === 'number' && (i.storyPoints as number) > 0)
    .sort((a, b) => {
      const pr = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]
      if (pr !== 0) return pr
      // Ready items ahead of not-ready at the same priority.
      const ra = isDefinitionOfReady(a) ? 0 : 1
      const rb = isDefinitionOfReady(b) ? 0 : 1
      if (ra !== rb) return ra - rb
      return (a.backlogRank ?? '').localeCompare(b.backlogRank ?? '')
    })

  const selected: SprintPlanItem[] = []
  let total = 0
  for (const i of estimated) {
    const pts = i.storyPoints as number
    if (selected.length > 0 && total + pts > capacityPoints) continue
    selected.push({
      id: i.id,
      key: i.key,
      title: i.title,
      itemType: i.itemType,
      priority: i.priority,
      storyPoints: pts,
      ready: isDefinitionOfReady(i),
    })
    total += pts
  }

  return { capacityPoints, totalPoints: total, selected, skippedUnestimated }
}

// ── refine_backlog: flag stale / oversized / unestimated / unready / dup ──

export interface RefinementFinding {
  key: string
  title: string
  detail: string
}
export interface BacklogRefinement {
  oversized: RefinementFinding[]
  unestimated: RefinementFinding[]
  unready: RefinementFinding[]
  stale: RefinementFinding[]
  duplicates: RefinementFinding[]
  healthy: number
  total: number
}

/**
 * Analyse a backlog for refinement gaps. `staleDays` items are those not
 * touched in that many days and not Done. Oversized = story points above the
 * top of the Fibonacci scale (needs splitting). Duplicates group on a
 * normalised title.
 */
export function analyzeBacklogRefinement(
  items: WorkItem[],
  opts?: { asOf?: Date; staleDays?: number },
): BacklogRefinement {
  const asOf = opts?.asOf ?? new Date()
  const staleDays = opts?.staleDays ?? 30
  const topScale = STORY_POINT_SCALE[STORY_POINT_SCALE.length - 1] as number

  const stories = items.filter((i) => i.itemType === 'story' || i.itemType === 'bug' || i.itemType === 'task')

  const oversized: RefinementFinding[] = items
    .filter((i) => typeof i.storyPoints === 'number' && (i.storyPoints as number) > topScale)
    .map((i) => ({ key: i.key, title: i.title, detail: `${i.storyPoints} points is above ${topScale}; split it.` }))

  const unestimated: RefinementFinding[] = stories
    .filter((i) => i.statusCategory !== 'done' && !(typeof i.storyPoints === 'number' && (i.storyPoints as number) > 0))
    .map((i) => ({ key: i.key, title: i.title, detail: 'No story points yet.' }))

  const unready: RefinementFinding[] = stories
    .filter((i) => i.statusCategory !== 'done' && !isDefinitionOfReady(i))
    .map((i) => {
      const gaps: string[] = []
      if (!(typeof i.storyPoints === 'number' && (i.storyPoints as number) > 0)) gaps.push('unestimated')
      if (i.acceptanceCriteria.length === 0) gaps.push('no acceptance criteria')
      if (i.blocked) gaps.push('blocked')
      return { key: i.key, title: i.title, detail: `Not ready: ${gaps.join(', ') || 'incomplete'}.` }
    })

  const stale: RefinementFinding[] = items
    .filter((i) => {
      if (i.statusCategory === 'done') return false
      const updated = i.updatedAt ? new Date(i.updatedAt) : null
      if (!updated || Number.isNaN(updated.getTime())) return false
      const days = (asOf.getTime() - updated.getTime()) / 86_400_000
      return days >= staleDays
    })
    .map((i) => ({ key: i.key, title: i.title, detail: `No change in ${staleDays}+ days.` }))

  // Duplicates: normalise the title and group; report every member of any
  // group of size >= 2.
  const byNorm = new Map<string, WorkItem[]>()
  for (const i of items) {
    const n = normaliseTitle(i.title)
    if (!n) continue
    const list = byNorm.get(n) ?? []
    list.push(i)
    byNorm.set(n, list)
  }
  const duplicates: RefinementFinding[] = []
  for (const group of byNorm.values()) {
    if (group.length < 2) continue
    const keys = group.map((g) => g.key).join(', ')
    for (const g of group) {
      duplicates.push({ key: g.key, title: g.title, detail: `Same title as ${keys}.` })
    }
  }

  const flagged = new Set<string>([
    ...oversized,
    ...unestimated,
    ...unready,
    ...stale,
    ...duplicates,
  ].map((f) => f.key))

  return {
    oversized,
    unestimated,
    unready,
    stale,
    duplicates,
    healthy: items.filter((i) => !flagged.has(i.key)).length,
    total: items.length,
  }
}

// ── detect_blockers: blocked items + dependency risk ─────────────────────

export interface BlockerFinding {
  key: string
  title: string
  reason: string
}
export interface BlockerScan {
  blocked: BlockerFinding[]
  dependencyRisk: BlockerFinding[]
  total: number
}

/**
 * Scan for blocked items (the `blocked` flag) and cross-item dependency risk:
 * any item that is blocked_by or depends_on another item that is not yet Done.
 */
export function scanBlockers(items: WorkItem[], links: WorkItemLink[]): BlockerScan {
  const byId = new Map(items.map((i) => [i.id, i]))

  const blocked: BlockerFinding[] = items
    .filter((i) => i.blocked)
    .map((i) => ({ key: i.key, title: i.title, reason: i.blockedReason?.trim() || 'Flagged blocked (no reason given).' }))

  const dependencyRisk: BlockerFinding[] = []
  for (const link of links) {
    if (link.linkType !== 'blocked_by' && link.linkType !== 'depends_on') continue
    const dependent = byId.get(link.sourceId)
    const upstream = byId.get(link.targetId)
    if (!dependent || !upstream) continue
    if (upstream.statusCategory !== 'done' && dependent.statusCategory !== 'done') {
      dependencyRisk.push({
        key: dependent.key,
        title: dependent.title,
        reason:
          link.linkType === 'depends_on'
            ? `Depends on ${upstream.key} (${upstream.title}), which is ${upstream.statusCategory}.`
            : `Blocked by ${upstream.key} (${upstream.title}), which is ${upstream.statusCategory}.`,
      })
    }
  }

  return { blocked, dependencyRisk, total: items.length }
}

// ── agile_board_query: natural-language filter over the backlog / board ──

export interface BoardQueryFilter {
  types: WorkItemType[]
  priority: Priority | null
  statusCategory: 'todo' | 'in_progress' | 'done' | null
  blocked: boolean
  epicText: string | null
  text: string | null
}

const TYPE_WORDS: { re: RegExp; type: WorkItemType }[] = [
  { re: /\bepics?\b/, type: 'epic' },
  { re: /\bfeatures?\b/, type: 'feature' },
  { re: /\bstor(y|ies)\b/, type: 'story' },
  { re: /\btasks?\b/, type: 'task' },
  { re: /\bbugs?\b/, type: 'bug' },
  { re: /\bspikes?\b/, type: 'spike' },
]

/** Parse a natural-language board query into a structured filter. */
export function buildBoardQueryFilter(query: string): BoardQueryFilter {
  const q = (query ?? '').toLowerCase()
  const types = TYPE_WORDS.filter((t) => t.re.test(q)).map((t) => t.type)

  let priority: Priority | null = null
  for (const p of ['critical', 'high', 'medium', 'low'] as Priority[]) {
    if (new RegExp(`\\b${p}\\b`).test(q)) {
      priority = p
      break
    }
  }

  let statusCategory: BoardQueryFilter['statusCategory'] = null
  if (/\bdone\b|\bcomplete[d]?\b/.test(q)) statusCategory = 'done'
  else if (/in[-\s]?progress|\bwip\b|in flight/.test(q)) statusCategory = 'in_progress'
  else if (/to[-\s]?do|\btodo\b|\bbacklog\b|not started/.test(q)) statusCategory = 'todo'

  const blocked = /\bblocked\b|\bblocker/.test(q)

  const epicMatch = query.match(/epic\s+["']?([^"'.,;]+?)["']?(?=\s*$|[.,;])/i)
  const epicText = epicMatch && epicMatch[1] ? epicMatch[1].trim() : null

  const textMatch = query.match(/(?:about|matching|containing|with)\s+["']?([^"'.,;]+?)["']?(?=\s*$|[.,;])/i)
  const text = textMatch && textMatch[1] ? textMatch[1].trim() : null

  return { types, priority, statusCategory, blocked, epicText, text }
}

/** Apply a parsed board query filter to the work items. */
export function applyBoardQuery(items: WorkItem[], filter: BoardQueryFilter): WorkItem[] {
  const epicIds = new Set<string>()
  if (filter.epicText) {
    const needle = filter.epicText.toLowerCase()
    for (const i of items) {
      if (i.itemType === 'epic' && i.title.toLowerCase().includes(needle)) epicIds.add(i.id)
    }
  }
  return items.filter((i) => {
    if (filter.types.length > 0 && !filter.types.includes(i.itemType)) return false
    if (filter.priority && i.priority !== filter.priority) return false
    if (filter.statusCategory && i.statusCategory !== filter.statusCategory) return false
    if (filter.blocked && !i.blocked) return false
    if (epicIds.size > 0 && !(i.parentId && epicIds.has(i.parentId)) && !epicIds.has(i.id)) return false
    if (filter.text && !`${i.title} ${i.description ?? ''}`.toLowerCase().includes(filter.text.toLowerCase())) return false
    return true
  })
}

// ── summarize_sprint: review notes + retrospective starter ───────────────

export interface SprintSummary {
  committedPoints: number
  completedPoints: number
  carriedPoints: number
  itemsDone: number
  itemsCarried: number
  completionPct: number
  reviewNotes: string
  retroNotes: string
}

/**
 * Draft sprint review notes and a retrospective starter from the sprint's
 * items and (optionally) the team's rolling velocity. All figures come from
 * the items; the prose is a grounded template the team can edit.
 */
export function draftSprintSummary(
  sprint: Sprint,
  items: WorkItem[],
  rollingVelocity?: number,
): SprintSummary {
  const scope = items.reduce((s, i) => s + (i.storyPoints ?? 0), 0)
  const done = items.filter((i) => i.statusCategory === 'done')
  const carried = items.filter((i) => i.statusCategory !== 'done')
  const completedPoints = done.reduce((s, i) => s + (i.storyPoints ?? 0), 0)
  const carriedPoints = carried.reduce((s, i) => s + (i.storyPoints ?? 0), 0)
  const committed = sprint.committedPoints ?? scope
  const completionPct = committed > 0 ? Math.round((completedPoints / committed) * 100) : 0

  const velocityClause =
    typeof rollingVelocity === 'number' && rollingVelocity > 0
      ? ` Recent rolling velocity is about ${Math.round(rollingVelocity)} points, so this sprint ran ${
          completedPoints >= rollingVelocity ? 'at or above' : 'below'
        } the team's typical pace.`
      : ''

  const reviewNotes =
    `Sprint "${sprint.name}"${sprint.goal ? ` (goal: ${sprint.goal})` : ''} completed ${done.length} of ${
      items.length
    } items, ${completedPoints} of ${committed} committed points (${completionPct}%).` +
    (carried.length > 0 ? ` ${carried.length} item(s) totalling ${carriedPoints} point(s) carry over.` : ' No carryover.') +
    velocityClause

  const retroLines = [
    'What went well: capture the practices worth keeping.',
    completionPct >= 90
      ? 'What to keep: the team hit its commitment, so protect whatever made that possible.'
      : 'What slowed us down: name the top one or two blockers that ate time this sprint.',
    carried.length > 0
      ? `Carryover to examine: ${carried
          .slice(0, 3)
          .map((i) => i.key)
          .join(', ')}${carried.length > 3 ? ', and others' : ''}. Was it scope, estimate, or a blocker?`
      : 'Estimation check: commitments matched delivery, confirm the estimates felt right.',
    'One improvement to try next sprint: agree a single concrete change and an owner.',
  ]

  return {
    committedPoints: committed,
    completedPoints,
    carriedPoints,
    itemsDone: done.length,
    itemsCarried: carried.length,
    completionPct,
    reviewNotes,
    retroNotes: retroLines.join('\n'),
  }
}

// ── Resolvers shared by handlers (match a work item / sprint loosely) ────

/** Find a work item by id, key, or a case-insensitive title match. */
export function findWorkItem(items: WorkItem[], ref: string | undefined): WorkItem | undefined {
  if (!ref) return undefined
  const r = ref.trim()
  if (!r) return undefined
  return (
    items.find((i) => i.id === r) ||
    items.find((i) => i.key.toLowerCase() === r.toLowerCase()) ||
    items.find((i) => i.title.toLowerCase() === r.toLowerCase()) ||
    items.find((i) => i.title.toLowerCase().includes(r.toLowerCase()))
  )
}

/**
 * Choose the sprint a tool should act on: an explicit ref wins, else the first
 * active sprint, else the first planned sprint, else the most recent closed.
 */
export function pickTargetSprint(sprints: Sprint[], ref?: string): Sprint | undefined {
  if (ref) {
    const r = ref.trim().toLowerCase()
    const byRef = sprints.find((s) => s.id.toLowerCase() === r || s.name.toLowerCase() === r || s.name.toLowerCase().includes(r))
    if (byRef) return byRef
  }
  return (
    sprints.find((s) => s.state === 'active') ||
    sprints.find((s) => s.state === 'planned') ||
    [...sprints].reverse().find((s) => s.state === 'closed')
  )
}
